import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  BlockOutlined,
  CancelOutlined,
  ContentCutOutlined,
  DeleteOutline,
  EditOutlined,
  LocalPrintshopOutlined,
  NoteAddOutlined,
  PointOfSaleOutlined,
  TaskAltOutlined,
} from "@mui/icons-material";
import { useParams } from "react-router-dom";

import CustomSnackBar from "../../Components/CustomSnackBar";
import MainText from "../../Components/MainText";
import SalePaymentDialog from "../../Components/sale/SalePaymentDialog";
import { printSaleTicket } from "../../Components/sale/SaleTicket";
import { APP_CONFIG } from "../../config/appConfig";
import { hasPermission } from "../../config/permissionUtils";
import { useAuth } from "../../context/AuthContext";
import { cashService, groomingService, saleService } from "../../services";
import { emitCashSessionChanged } from "../../services/http/cashEvents";
import GroomingDiagnosticDialog from "./components/GroomingDiagnosticDialog";

const LINE_TYPE_OPTIONS = [
  { value: "SERVICE", label: "Servicio" },
  { value: "EXTRA", label: "Extra" },
  { value: "DISCOUNT", label: "Descuento" },
  { value: "ADJUSTMENT", label: "Ajuste" },
];

const buildEmptyLineForm = () => ({
  line_type: "SERVICE",
  service_type_id: "",
  description: "",
  notes: "",
  quantity: "1",
  unit_price: "0.00",
  estimated_duration_minutes: "",
  override_reason: "",
});

const DURATION_PRESETS = [15, 30, 45, 60, 90];

const toMoney = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "Q0.00";
  return `Q${parsed.toFixed(2)}`;
};

const toAmount = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getLineNumericTotal = (line) =>
  toAmount(line?.quantity) * toAmount(line?.unit_price);

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("es-GT", {
    timeZone: APP_CONFIG.timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const extractErrorMessage = (error, fallback = "No se pudo completar la operacion.") => {
  const data = error?.response?.data;
  if (!data || typeof data !== "object") return fallback;
  if (typeof data.detail === "string" && data.detail.trim()) return data.detail;
  const firstKey = Object.keys(data)[0];
  const firstValue = data[firstKey];
  if (Array.isArray(firstValue) && firstValue[0]) return String(firstValue[0]);
  if (typeof firstValue === "string" && firstValue.trim()) return firstValue;
  return fallback;
};

const hasDiagnosticData = (assessment) => {
  if (!assessment) return false;
  const fields = [
    "coat_condition",
    "matting_level",
    "nail_condition",
    "skin_condition",
    "behavior_condition",
    "notes",
    "recommended_work",
  ];
  return fields.some((field) => String(assessment?.[field] || "").trim().length > 0);
};

const getOrderStatusMeta = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "EN_PROCESO") return { label: "En proceso", color: "warning" };
  if (normalized === "LISTO_ENTREGA") return { label: "Listo para cobrar", color: "info" };
  if (normalized === "COBRADO") return { label: "Cobrado", color: "success" };
  if (normalized === "CANCELADO") return { label: "Cancelado", color: "error" };
  if (normalized === "DRAFT") return { label: "En proceso", color: "warning" };
  if (normalized === "RECEIVED") return { label: "Recibido", color: "info" };
  if (normalized === "IN_PROGRESS") return { label: "En proceso", color: "warning" };
  if (normalized === "READY") return { label: "Listo para cobrar", color: "info" };
  if (normalized === "DELIVERED") return { label: "Entregado", color: "success" };
  if (normalized === "CANCELED") return { label: "Cancelado", color: "error" };
  if (normalized === "PAUSED") return { label: "Pausado", color: "warning" };
  return { label: "En proceso", color: "default" };
};

const getPaymentStatusMeta = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "PAID") return { label: "Pagado", color: "success" };
  if (normalized === "PARTIAL") return { label: "Parcial", color: "warning" };
  if (normalized === "VOID") return { label: "Anulado", color: "error" };
  return { label: "Pendiente", color: "default" };
};

const getAppointmentStatusMeta = (appointment, orderUiStatus = "") => {
  const normalized = String(appointment?.status || appointment || "").toUpperCase();
  const normalizedOrderUiStatus = String(orderUiStatus || "").toUpperCase();
  if (normalized === "CONFIRMED") return { label: "Confirmada", color: "info" };
  if (normalized === "CHECKED_IN") return { label: "Recepcionada", color: "warning" };
  if (normalized === "CONVERTED") {
    if (normalizedOrderUiStatus === "COBRADO") return { label: "Finalizado", color: "success" };
    if (normalizedOrderUiStatus === "CANCELADO") return { label: "Cancelada", color: "error" };
    return { label: "En proceso", color: "info" };
  }
  if (normalized === "COMPLETED") return { label: "Completada", color: "success" };
  if (normalized === "CANCELED") return { label: "Cancelada", color: "error" };
  if (normalized === "NO_SHOW") return { label: "No llego", color: "default" };
  return { label: "Programada", color: "default" };
};

const getSoldBy = (sale) =>
  sale?.finalized_by_name ||
  sale?.charged_by_name ||
  sale?.paid_by_name ||
  sale?.sold_by_display ||
  sale?.sold_by_name ||
  sale?.sold_by?.name ||
  sale?.created_by_name ||
  sale?.created_by?.name ||
  "-";

const isSalePaid = (sale) =>
  ["COMPLETED", "RETURNED"].includes(String(sale?.status || "").toUpperCase());

const isSaleVoidedOrCanceled = (sale) =>
  ["VOIDED", "CANCELED"].includes(String(sale?.status || "").toUpperCase());

const resolveOrderUiStatus = (order) => {
  if (!order) return "EN_PROCESO";
  const rawUi = String(order?.ui_status || "").toUpperCase();
  if (["EN_PROCESO", "LISTO_ENTREGA", "COBRADO", "CANCELADO"].includes(rawUi)) return rawUi;
  if (isSaleVoidedOrCanceled(order?.sale)) return "CANCELADO";
  if (String(order?.status || "").toUpperCase() === "CANCELED" || order?.canceled_at) return "CANCELADO";
  if (Boolean(order?.is_paid) || isSalePaid(order?.sale)) return "COBRADO";
  if (["READY", "DELIVERED"].includes(String(order?.status || "").toUpperCase())) return "LISTO_ENTREGA";
  return "EN_PROCESO";
};

const isOrderArtifactEditable = (order) => {
  if (!order) return false;
  if (order?.is_paid || isSalePaid(order?.sale)) return false;
  if (isSaleVoidedOrCanceled(order?.sale)) return false;
  if (String(order?.status || "").toUpperCase() === "CANCELED") return false;
  if (order?.canceled_at) return false;
  return true;
};

const hasValidChargeableGroomingLines = (order, lines) => {
  const rows = Array.isArray(lines) ? lines : [];
  const total = toAmount(order?.total);
  return (
    total > 0 &&
    rows.length > 0 &&
    rows.every((line) => toAmount(line?.unit_price) > 0 && toAmount(line?.quantity) > 0 && toAmount(line?.total) > 0)
  );
};

function SummaryBlock({ label, value }) {
  return (
    <Box
      sx={(theme) => ({
        p: 1.35,
        borderRadius: 1.5,
        bgcolor: theme.palette.action.hover,
      })}
    >
      <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{label}</Typography>
      <Typography sx={{ mt: 0.35, fontSize: 13, fontWeight: 600 }}>{value || "-"}</Typography>
    </Box>
  );
}

function GroomingOrderDetailPage() {
  const { orderId } = useParams();
  const { currentBranch, effectivePerms = [], isSuperuser, user } = useAuth();
  const branchId = currentBranch?.id || null;

  const canViewPage = hasPermission(effectivePerms, "grooming_view", isSuperuser);
  const canManage = hasPermission(effectivePerms, "grooming_manage", isSuperuser);
  const canPayNow = hasPermission(effectivePerms, "sale_pay_now", isSuperuser);
  const canLowerPrice = hasPermission(effectivePerms, "sale_discount_override", isSuperuser);

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionSubmitting, setActionSubmitting] = useState("");
  const [cashStatusKnown, setCashStatusKnown] = useState(false);
  const [hasOpenCashSession, setHasOpenCashSession] = useState(false);

  const [lineDialog, setLineDialog] = useState({ open: false, mode: "create", line: null });
  const [lineForm, setLineForm] = useState(buildEmptyLineForm());
  const [serviceTypes, setServiceTypes] = useState([]);
  const [lineSubmitting, setLineSubmitting] = useState(false);
  const [itemRequestPending, setItemRequestPending] = useState(false);

  const [noteDraft, setNoteDraft] = useState("");
  const [noteInternal, setNoteInternal] = useState(true);
  const [noteSubmitting, setNoteSubmitting] = useState(false);
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [paySubmitting, setPaySubmitting] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [transferAccounts, setTransferAccounts] = useState([]);
  const [loadingTransferAccounts, setLoadingTransferAccounts] = useState(false);

  const [noteDialog, setNoteDialog] = useState({
    open: false,
    noteId: null,
    note: "",
    is_internal: true,
  });
  const [noteDialogSubmitting, setNoteDialogSubmitting] = useState(false);
  const [diagnosticDialog, setDiagnosticDialog] = useState(false);

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: "",
    title: "",
    description: "",
    confirmLabel: "Confirmar",
    color: "primary",
    value: "",
    lineId: null,
  });
  const [pendingLineMutation, setPendingLineMutation] = useState(null);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const showSnack = useCallback((message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  }, []);

  const loadCashStatus = useCallback(async () => {
    if (!branchId) {
      setCashStatusKnown(false);
      setHasOpenCashSession(false);
      return;
    }
    try {
      const data = await cashService.currentSession({ branch_id: branchId });
      const hasOpenSession = Boolean(data?.session && data.session.status === "OPEN");
      setCashStatusKnown(true);
      setHasOpenCashSession(hasOpenSession);
    } catch {
      setCashStatusKnown(false);
      setHasOpenCashSession(false);
    }
  }, [branchId]);

  const loadTransferAccounts = useCallback(async () => {
    if (!branchId) {
      setTransferAccounts([]);
      return;
    }
    setLoadingTransferAccounts(true);
    try {
      const data = await saleService.listTransferAccounts({ branch_id: branchId, active: true });
      setTransferAccounts(Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : []);
    } catch {
      setTransferAccounts([]);
    } finally {
      setLoadingTransferAccounts(false);
    }
  }, [branchId]);

  const loadOrder = useCallback(async () => {
    if (!branchId || !orderId || !canViewPage) {
      setOrder(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await groomingService.getOrder(orderId, { branch_id: branchId });
      setOrder(data || null);
    } catch (error) {
      setOrder(null);
      showSnack(extractErrorMessage(error, "No se pudo cargar la orden grooming."), "error");
    } finally {
      setLoading(false);
    }
  }, [branchId, canViewPage, orderId, showSnack]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  useEffect(() => {
    if (!branchId || !canViewPage) {
      setServiceTypes([]);
      return;
    }
    groomingService
      .listServiceTypes({ branch_id: branchId, active: "true" })
      .then((data) => setServiceTypes(Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : []))
      .catch(() => setServiceTypes([]));
  }, [branchId, canViewPage]);

  useEffect(() => {
    loadCashStatus();
  }, [loadCashStatus]);

  useEffect(() => {
    loadTransferAccounts();
  }, [loadTransferAccounts]);

  const saleStatus = String(order?.sale?.status || "").toUpperCase();
  const orderIsPaid = Boolean(order?.is_paid) || isSalePaid(order?.sale);
  const orderUiStatus = resolveOrderUiStatus(order);
  const saleCanBeCharged =
    orderUiStatus === "LISTO_ENTREGA" &&
    !orderIsPaid &&
    !["CANCELED", "VOIDED"].includes(saleStatus);
  const canEditArtifacts = canManage && isOrderArtifactEditable(order);
  const canEditItems = canEditArtifacts && ["EN_PROCESO", "LISTO_ENTREGA"].includes(orderUiStatus);
  const appointment = order?.appointment_detail || order?.appointment || null;
  const lines = useMemo(() => (Array.isArray(order?.lines) ? order.lines : []), [order?.lines]);
  const hasValidChargeableLines = useMemo(
    () => hasValidChargeableGroomingLines(order, lines),
    [lines, order]
  );
  const notes = Array.isArray(order?.notes) ? order.notes : [];
  const selectedLineService = useMemo(
    () => serviceTypes.find((item) => String(item.id) === String(lineForm.service_type_id)) || null,
    [lineForm.service_type_id, serviceTypes]
  );
  const hasRegisteredDiagnostic = useMemo(
    () => hasDiagnosticData(order?.assessment || null),
    [order?.assessment]
  );

  const canEditNote = useCallback(
    (note) => {
      if (!canEditArtifacts || !note?.id) return false;
      if (isSuperuser) return true;
      return Number(note?.author?.id) === Number(user?.id);
    },
    [canEditArtifacts, isSuperuser, user?.id]
  );

  const orderStatusMeta = getOrderStatusMeta(orderUiStatus);
  const paymentStatusMeta = getPaymentStatusMeta(order?.payment_status);
  const appointmentStatusMeta = getAppointmentStatusMeta(appointment, orderUiStatus);

  const availableActions = useMemo(() => {
    if (!order || !canManage) return [];
    const actions = [];

    if (orderUiStatus === "EN_PROCESO" && canEditArtifacts) {
      actions.push({ key: "finalize", label: "Finalizar servicio", icon: <TaskAltOutlined /> });
    }
    if (["EN_PROCESO", "LISTO_ENTREGA"].includes(orderUiStatus)) {
      actions.push({
        key: "cancel",
        label: "Cancelar/Anular",
        icon: <CancelOutlined />,
        color: "error",
      });
    }
    if (orderUiStatus === "COBRADO" && order?.sale?.id && isSuperuser) {
      actions.push({
        key: "void-sale",
        label: "Anular venta",
        icon: <BlockOutlined />,
        color: "error",
      });
    }
    return actions;
  }, [canEditArtifacts, canManage, isSuperuser, order, orderUiStatus]);

  const openLineCreateDialog = useCallback(() => {
    setLineForm(buildEmptyLineForm());
    setLineDialog({ open: true, mode: "create", line: null });
  }, []);

  const openDiagnosticDialog = useCallback(() => {
    setDiagnosticDialog(true);
  }, []);

  const openLineEditDialog = useCallback((line) => {
    setLineForm({
      line_type: String(line?.line_type || "SERVICE"),
      service_type_id: String(line?.service_type_id || line?.service_type?.id || ""),
      description: String(line?.description || ""),
      notes: String(line?.notes || ""),
      quantity: String(line?.quantity || "1"),
      unit_price: String(line?.unit_price || "0.00"),
      estimated_duration_minutes:
        line?.estimated_duration_minutes === 0 || line?.estimated_duration_minutes
          ? String(line.estimated_duration_minutes)
          : "",
      override_reason: String(line?.override_reason || ""),
    });
    setLineDialog({ open: true, mode: "edit", line });
  }, []);

  const closeLineDialog = useCallback(() => {
    if (lineSubmitting) return;
    setLineDialog({ open: false, mode: "create", line: null });
    setLineForm(buildEmptyLineForm());
  }, [lineSubmitting]);

  const handleLineFormChange = useCallback(
    (field) => (event) => {
      setLineForm((prev) => ({ ...prev, [field]: event.target.value }));
    },
    []
  );

  const openDeleteLineDialog = useCallback((line) => {
    const currentTotal = toAmount(order?.total);
    const nextTotal = currentTotal - getLineNumericTotal(line);
    if (nextTotal < (currentTotal - 0.0001)) {
      setPendingLineMutation({
        mode: "delete",
        lineId: line?.id || null,
      });
      setConfirmDialog({
        open: true,
        type: "reduce-total",
        title: "Confirmar reduccion de total",
        description: "Estás reduciendo el total de la orden. ¿Deseas continuar?",
        confirmLabel: "Continuar",
        color: "warning",
        value: "",
        lineId: line?.id || null,
      });
      return;
    }
    setConfirmDialog({
      open: true,
      type: "delete-line",
      title: "Eliminar linea grooming",
      description: `Se eliminara la linea "${line?.description || "sin descripcion"}".`,
      confirmLabel: "Eliminar",
      color: "error",
      value: "",
      lineId: line?.id || null,
    });
  }, [order?.total]);

  const openCancelDialog = useCallback(() => {
    setConfirmDialog({
      open: true,
      type: "cancel-order",
      title: "Cancelar orden grooming",
      description: "Debes registrar una razon obligatoria para cancelar la orden.",
      confirmLabel: "Cancelar orden",
      color: "error",
      value: "",
      lineId: null,
    });
  }, []);

  const openVoidSaleDialog = useCallback(() => {
    setConfirmDialog({
      open: true,
      type: "void-sale",
      title: "Anular venta grooming",
      description: "Debes registrar una razon obligatoria para anular la venta cobrada.",
      confirmLabel: "Anular venta",
      color: "error",
      value: "",
      lineId: null,
    });
  }, []);

  const closeConfirmDialog = useCallback(() => {
    if (actionSubmitting) return;
    setPendingLineMutation(null);
    setConfirmDialog({
      open: false,
      type: "",
      title: "",
      description: "",
      confirmLabel: "Confirmar",
      color: "primary",
      value: "",
      lineId: null,
    });
  }, [actionSubmitting]);

  const closeNoteDialog = useCallback(() => {
    if (noteDialogSubmitting) return;
    setNoteDialog({
      open: false,
      noteId: null,
      note: "",
      is_internal: true,
    });
  }, [noteDialogSubmitting]);

  const handleTransition = useCallback(
    async (type) => {
      if (!branchId || !order?.id) return;
      if (type === "finalize" && !hasValidChargeableGroomingLines(order, lines)) {
        showSnack("Agrega servicios con precio válido antes de finalizar/cobrar.", "warning");
        return;
      }
      setActionSubmitting(type);
      try {
        if (type === "finalize") {
          const response = await groomingService.readyOrder(order.id, { branch_id: branchId });
          if (response?.order) {
            setOrder(response.order);
          }
          showSnack("Servicio finalizado. La orden quedo lista para cobrar.", "success");
        }
        await loadOrder();
      } catch (error) {
        showSnack(extractErrorMessage(error, "No se pudo actualizar la orden."), "error");
      } finally {
        setActionSubmitting("");
      }
    },
    [branchId, lines, loadOrder, order, showSnack]
  );

  const handleConfirmDialogAction = useCallback(async () => {
    if (!branchId || !order?.id || !confirmDialog.type) return;
    setActionSubmitting(confirmDialog.type);
    const isLineMutationAction =
      confirmDialog.type === "delete-line" || confirmDialog.type === "reduce-total";
    if (isLineMutationAction) {
      setItemRequestPending(true);
    }
    try {
      if (confirmDialog.type === "reduce-total" && pendingLineMutation) {
        const reason = String(confirmDialog.value || "").trim();
        if (!reason) {
          showSnack("Debes ingresar la razon de reduccion.", "warning");
          return;
        }
        if (pendingLineMutation.mode === "delete" && pendingLineMutation.lineId) {
          const response = await groomingService.deleteOrderLine(order.id, pendingLineMutation.lineId, branchId, {
            reason,
          });
          if (response?.order) {
            setOrder({
              ...response.order,
              lines: Array.isArray(response?.lines) ? response.lines : response.order.lines,
            });
          }
          showSnack("Linea eliminada correctamente.", "success");
        } else if (
          pendingLineMutation.mode === "edit" &&
          pendingLineMutation.lineId &&
          pendingLineMutation.payload
        ) {
          const response = await groomingService.updateOrderLine(
            order.id,
            pendingLineMutation.lineId,
            { ...pendingLineMutation.payload, reduction_reason: reason }
          );
          if (response?.order) {
            setOrder({
              ...response.order,
              lines: Array.isArray(response?.lines) ? response.lines : response.order.lines,
            });
          }
          showSnack("Linea actualizada correctamente.", "success");
          closeLineDialog();
        }
      } else if (confirmDialog.type === "delete-line" && confirmDialog.lineId) {
        const response = await groomingService.deleteOrderLine(order.id, confirmDialog.lineId, branchId);
        if (response?.order) {
          setOrder({
            ...response.order,
            lines: Array.isArray(response?.lines) ? response.lines : response.order.lines,
          });
        }
        showSnack("Linea eliminada correctamente.", "success");
      } else if (confirmDialog.type === "cancel-order") {
        const reason = String(confirmDialog.value || "").trim();
        if (!reason) {
          showSnack("Debes ingresar la razon de cancelacion.", "warning");
          return;
        }
        await groomingService.cancelOrder(order.id, {
          branch_id: branchId,
          reason,
          notes: reason,
        });
        showSnack("Orden cancelada correctamente.", "success");
      } else if (confirmDialog.type === "void-sale") {
        const reason = String(confirmDialog.value || "").trim();
        if (!reason) {
          showSnack("Debes ingresar la razon de anulacion.", "warning");
          return;
        }
        if (!order?.sale?.id) {
          showSnack("No hay venta relacionada para anular.", "warning");
          return;
        }
        await saleService.voidSale(order.sale.id, {
          branch_id: branchId,
          reason,
        });
        emitCashSessionChanged({ branchId, action: "sale_voided", saleId: order.sale.id });
        showSnack("Venta grooming anulada correctamente.", "success");
      }
      closeConfirmDialog();
      await loadOrder();
    } catch (error) {
      showSnack(extractErrorMessage(error, "No se pudo completar la accion."), "error");
    } finally {
      if (isLineMutationAction) {
        setItemRequestPending(false);
      }
      setActionSubmitting("");
    }
  }, [
    branchId,
    closeLineDialog,
    closeConfirmDialog,
    confirmDialog.lineId,
    confirmDialog.type,
    confirmDialog.value,
    loadOrder,
    order?.id,
    order?.sale?.id,
    pendingLineMutation,
    showSnack,
  ]);

  const handleSaveLine = useCallback(async () => {
    if (!branchId || !order?.id) return;
    if (!canEditItems) {
      showSnack("Solo puedes modificar items cuando la orden no esta cobrada ni cancelada.", "warning");
      return;
    }
    const description = String(lineForm.description || "").trim();
    const quantity = Number(lineForm.quantity);
    const unitPrice = Number(lineForm.unit_price);
    const estimatedDuration =
      lineForm.estimated_duration_minutes === ""
        ? undefined
        : Math.max(0, Number(lineForm.estimated_duration_minutes));
    const selectedService = serviceTypes.find((item) => String(item.id) === String(lineForm.service_type_id));
    const overrideReason = String(lineForm.override_reason || "").trim();

    if (lineForm.line_type === "SERVICE" && !selectedService) {
      showSnack("Debes seleccionar un servicio del catalogo.", "warning");
      return;
    }
    if (!description) {
      showSnack("La descripcion de la linea es requerida.", "warning");
      return;
    }
    if (!Number.isFinite(quantity) || quantity <= 0) {
      showSnack("La cantidad debe ser mayor que cero.", "warning");
      return;
    }
    if (!Number.isFinite(unitPrice) || unitPrice <= 0) {
      showSnack("El precio unitario debe ser mayor que cero.", "warning");
      return;
    }
    if (["EXTRA", "ADJUSTMENT"].includes(lineForm.line_type) && !overrideReason) {
      showSnack("Debes indicar motivo para el cargo manual.", "warning");
      return;
    }
    if (selectedService && unitPrice < Number(selectedService.base_price || 0) && !overrideReason) {
      showSnack("Debes indicar motivo para bajar la tarifa base.", "warning");
      return;
    }
    if (selectedService && unitPrice < Number(selectedService.base_price || 0) && !canLowerPrice) {
      showSnack("No autorizado para bajar la tarifa base.", "warning");
      return;
    }

    const payload = {
      branch_id: branchId,
      line_type: lineForm.line_type,
      service_type_id: lineForm.line_type === "SERVICE" ? selectedService?.id : null,
      description,
      notes: String(lineForm.notes || "").trim(),
      quantity,
      unit_price: unitPrice,
      estimated_duration_minutes: estimatedDuration,
      override_reason: overrideReason,
    };

    if (lineDialog.mode === "edit" && lineDialog.line?.id) {
      const currentTotal = toAmount(order?.total);
      const previousLineTotal = getLineNumericTotal(lineDialog.line);
      const nextLineTotal = quantity * unitPrice;
      const projectedTotal = currentTotal - previousLineTotal + nextLineTotal;
      if (projectedTotal < (currentTotal - 0.0001)) {
        setPendingLineMutation({
          mode: "edit",
          lineId: lineDialog.line.id,
          payload,
        });
        setConfirmDialog({
          open: true,
          type: "reduce-total",
          title: "Confirmar reduccion de total",
          description: "Estás reduciendo el total de la orden. ¿Deseas continuar?",
          confirmLabel: "Continuar",
          color: "warning",
          value: "",
          lineId: lineDialog.line.id,
        });
        return;
      }
    }

    setLineSubmitting(true);
    setItemRequestPending(true);
    try {
      if (lineDialog.mode === "edit" && lineDialog.line?.id) {
        const response = await groomingService.updateOrderLine(order.id, lineDialog.line.id, payload);
        if (response?.order) {
          setOrder({
            ...response.order,
            lines: Array.isArray(response?.lines) ? response.lines : response.order.lines,
          });
        }
        showSnack("Linea actualizada correctamente.", "success");
      } else {
        const response = await groomingService.createOrderLine(order.id, payload);
        if (response?.order) {
          setOrder({
            ...response.order,
            lines: Array.isArray(response?.lines) ? response.lines : response.order.lines,
          });
        }
        showSnack("Linea creada correctamente.", "success");
      }
      closeLineDialog();
      await loadOrder();
    } catch (error) {
      showSnack(extractErrorMessage(error, "No se pudo guardar la linea."), "error");
    } finally {
      setItemRequestPending(false);
      setLineSubmitting(false);
    }
  }, [branchId, canEditItems, canLowerPrice, closeLineDialog, lineDialog.line, lineDialog.mode, lineForm, loadOrder, order?.id, order?.total, serviceTypes, showSnack]);

  const handleCreateNote = useCallback(async () => {
    if (!branchId || !order?.id) return;
    const note = String(noteDraft || "").trim();
    if (!note) {
      showSnack("La nota no puede ir vacia.", "warning");
      return;
    }
    setNoteSubmitting(true);
    try {
      await groomingService.createOrderNote(order.id, {
        branch_id: branchId,
        note,
        is_internal: noteInternal,
      });
      setNoteDraft("");
      setNoteInternal(true);
      showSnack("Nota creada correctamente.", "success");
      await loadOrder();
    } catch (error) {
      showSnack(extractErrorMessage(error, "No se pudo crear la nota."), "error");
    } finally {
      setNoteSubmitting(false);
    }
  }, [branchId, loadOrder, noteDraft, noteInternal, order?.id, showSnack]);

  const openNoteEditDialog = useCallback((note) => {
    setNoteDialog({
      open: true,
      noteId: note?.id || null,
      note: String(note?.note || ""),
      is_internal: Boolean(note?.is_internal),
    });
  }, []);

  const handleSaveNoteEdit = useCallback(async () => {
    if (!branchId || !order?.id || !noteDialog.noteId) return;
    const note = String(noteDialog.note || "").trim();
    if (!note) {
      showSnack("La nota no puede ir vacia.", "warning");
      return;
    }
    setNoteDialogSubmitting(true);
    try {
      await groomingService.updateOrderNote(order.id, noteDialog.noteId, {
        branch_id: branchId,
        note,
        is_internal: noteDialog.is_internal,
      });
      showSnack("Nota actualizada correctamente.", "success");
      closeNoteDialog();
      await loadOrder();
    } catch (error) {
      showSnack(extractErrorMessage(error, "No se pudo actualizar la nota."), "error");
    } finally {
      setNoteDialogSubmitting(false);
    }
  }, [branchId, closeNoteDialog, loadOrder, noteDialog.is_internal, noteDialog.note, noteDialog.noteId, order?.id, showSnack]);

  const handlePrintSaleTicket = useCallback(async () => {
    if (!branchId || !order?.sale?.id) return;
    if (!isSalePaid(order?.sale)) {
      showSnack("El ticket solo esta disponible despues del cobro confirmado.", "warning");
      return;
    }
    try {
      const sale = await saleService.getSale(order.sale.id, { branch_id: branchId });
      printSaleTicket({
        sale,
        branchName: sale?.branch_name || currentBranch?.name || "-",
        cashierName: getSoldBy(sale),
      });
      showSnack("Ticket enviado a impresion.", "success");
    } catch (error) {
      showSnack(extractErrorMessage(error, "No se pudo imprimir el ticket."), "error");
    }
  }, [branchId, currentBranch?.name, order?.sale, showSnack]);

  const handleOpenPayDialog = useCallback(async () => {
    if (!branchId || !order?.id) return;
    if (itemRequestPending || lineSubmitting) {
      showSnack("Espera a que terminen los cambios de items antes de cobrar.", "warning");
      return;
    }
    if (!hasValidChargeableGroomingLines(order, lines)) {
      showSnack("Agrega servicios con precio válido antes de finalizar/cobrar.", "warning");
      return;
    }
    if (!canPayNow) {
      showSnack("No autorizado para cobrar al instante.", "warning");
      return;
    }
    if (!hasOpenCashSession) {
      showSnack("Abre caja para cobrar esta orden.", "warning");
      return;
    }
    try {
      const latestOrder = await groomingService.getOrder(order.id, { branch_id: branchId });
      const baseOrder = latestOrder || order;
      setOrder(baseOrder);
      let saleId = baseOrder?.sale?.id || null;
      let updatedOrder = baseOrder;
      if (!saleId) {
        const converted = await groomingService.convertOrderToSale(baseOrder.id, {
          branch_id: branchId,
        });
        updatedOrder = converted?.order || updatedOrder;
        saleId = converted?.sale?.id || updatedOrder?.sale?.id || null;
        if (!saleId) {
          throw new Error("No se pudo crear la venta para cobrar.");
        }
        setOrder(updatedOrder);
      }
      const sale = await saleService.getSale(saleId, { branch_id: branchId });
      setSelectedSale(sale || null);
      setPayDialogOpen(true);
    } catch (error) {
      showSnack(extractErrorMessage(error, "No se pudo cargar la venta para cobrar."), "error");
    }
  }, [branchId, canPayNow, hasOpenCashSession, itemRequestPending, lineSubmitting, lines, order, showSnack]);

  const handleConfirmPay = useCallback(
    async (paymentPayload) => {
      if (!branchId || !selectedSale?.id) return;
      const method = String(paymentPayload?.payment_method || "CASH").toUpperCase();
      const total = Number(selectedSale?.total || 0);
      const referenceParts = [paymentPayload?.reference || ""];
      if (paymentPayload?.bank) referenceParts.push(`Banco: ${paymentPayload.bank}`);
      if (paymentPayload?.payment_date) referenceParts.push(`Fecha: ${paymentPayload.payment_date}`);
      const composedReference = referenceParts.filter(Boolean).join(" | ");

      setPaySubmitting(true);
      try {
        const updated = await saleService.paySale(selectedSale.id, {
          branch_id: branchId,
          payments: [
            {
              payment_method: method,
              amount: total.toFixed(2),
              cash_received: method === "CASH" ? paymentPayload?.cash_received || total.toFixed(2) : undefined,
              reference: composedReference,
              account_id: method === "TRANSFER" ? paymentPayload?.account_id : undefined,
            },
          ],
        });
        setPayDialogOpen(false);
        setSelectedSale(updated || null);
        const nextStatus = String(updated?.status || "").toUpperCase();
        if (nextStatus === "WAITING_PAYMENT") {
          showSnack("Transferencia registrada. Quedo esperando aprobacion.", "info");
          emitCashSessionChanged({ branchId, action: "sale_waiting_payment", saleId: selectedSale.id });
        } else {
          showSnack("Cobro registrado correctamente.", "success");
          emitCashSessionChanged({ branchId, action: "sale_paid", saleId: selectedSale.id });
        }
        await Promise.all([loadOrder(), loadCashStatus()]);
      } catch (error) {
        showSnack(extractErrorMessage(error, "No se pudo cobrar la venta grooming."), "error");
      } finally {
        setPaySubmitting(false);
      }
    },
    [branchId, loadCashStatus, loadOrder, selectedSale?.id, selectedSale?.total, showSnack]
  );

  if (!canViewPage) {
    return <Alert severity="warning">No tienes permisos para ver ordenes grooming.</Alert>;
  }

  if (!branchId) {
    return <Alert severity="info">Selecciona una sucursal para operar grooming.</Alert>;
  }

  if (loading) {
    return <Typography>Cargando orden grooming...</Typography>;
  }

  if (!order) {
    return <Alert severity="error">No se encontro la orden grooming.</Alert>;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.5}>
        <MainText
          title={`Orden grooming #${order.id}`}
          subtitle={`${order.pet?.name || "Mascota"} - ${order.customer?.name || "Cliente"}`}
          icon={ContentCutOutlined}
        />
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
          {availableActions.map((action) => (
            <Button
              key={action.key}
              variant={action.color === "error" ? "outlined" : "contained"}
              color={action.color || "primary"}
              startIcon={action.icon}
              disabled={Boolean(actionSubmitting)}
              onClick={() => {
                if (action.key === "cancel") {
                  openCancelDialog();
                } else if (action.key === "void-sale") {
                  openVoidSaleDialog();
                } else {
                  handleTransition(action.key);
                }
              }}
            >
              {actionSubmitting === action.key ? "Procesando..." : action.label}
            </Button>
          ))}
          {isSalePaid(order?.sale) ? (
            <Button
              variant="outlined"
              startIcon={<LocalPrintshopOutlined />}
              onClick={handlePrintSaleTicket}
            >
              Ver ticket
            </Button>
          ) : null}
          {saleCanBeCharged ? (
            <Button
              variant="contained"
              color="success"
              startIcon={<PointOfSaleOutlined />}
              disabled={
                Boolean(actionSubmitting) ||
                !canPayNow ||
                !hasOpenCashSession ||
                !hasValidChargeableLines ||
                lineSubmitting ||
                itemRequestPending
              }
              onClick={handleOpenPayDialog}
            >
              Cobrar
            </Button>
          ) : null}
        </Stack>
      </Stack>

      {!canEditArtifacts ? (
        <Alert severity="info">
          Esta orden ya no admite cambios en detalle, lineas ni observaciones porque ya fue cobrada o cerrada.
        </Alert>
      ) : null}
      {canEditArtifacts && orderUiStatus === "LISTO_ENTREGA" ? (
        <Alert severity="info">
          La orden esta lista para cobrar. Puedes ajustar items antes del cobro si es necesario.
        </Alert>
      ) : null}

      {cashStatusKnown && !hasOpenCashSession && saleCanBeCharged ? (
        <Alert severity="warning">
          Abre caja para cobrar esta orden.
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Chip label={orderStatusMeta.label} color={orderStatusMeta.color} />
            <Chip variant="outlined" label={`Cobro: ${paymentStatusMeta.label}`} color={paymentStatusMeta.color} />
            <Chip variant="outlined" label={`Sucursal: ${order.branch?.name || "-"}`} />
            <Chip variant="outlined" label={`Asignado: ${order.assigned_to?.name || "-"}`} />
            {order?.sale?.id ? (
              <Chip color="success" label={`Venta: ${order.sale.sale_number || `#${order.sale.id}`}`} />
            ) : (
              <Chip variant="outlined" label="Sin venta enlazada" />
            )}
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" },
              gap: 1.2,
            }}
          >
            <SummaryBlock label="Mascota" value={order.pet?.name} />
            <SummaryBlock label="Cliente" value={order.customer?.name} />
            <SummaryBlock label="Telefono" value={order.customer?.phone_number} />
            <SummaryBlock
              label="Cita"
              value={appointment?.id ? formatDateTime(appointment.scheduled_start) : "Sin cita"}
            />
            <SummaryBlock label="Creada" value={formatDateTime(order.created_at)} />
            <SummaryBlock label="Total" value={toMoney(order.total)} />
            <SummaryBlock label="Estado cita" value={appointment?.id ? appointmentStatusMeta.label : "Sin cita"} />
            <SummaryBlock label="Estado cobro" value={paymentStatusMeta.label} />
            <SummaryBlock label="Cobrado por" value={order?.sale?.id ? getSoldBy(order.sale) : "-"} />
          </Box>

          <Stack spacing={0.6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Timeline
            </Typography>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
              {[
                { label: "Recepcion", value: order.check_in_at },
                { label: "Inicio", value: order.started_at },
                { label: "Lista", value: order.ready_at },
                { label: "Entrega", value: order.delivered_at },
              ]
                .filter((entry) => Boolean(entry.value))
                .map((entry) => (
                  <Chip
                    key={entry.label}
                    size="small"
                    variant="outlined"
                    label={`${entry.label}: ${formatDateTime(entry.value)}`}
                  />
                ))}
              {!order.check_in_at && !order.started_at && !order.ready_at && !order.delivered_at ? (
                <Typography variant="caption" color="text.secondary">
                  Aun no hay eventos de atencion registrados.
                </Typography>
              ) : null}
            </Stack>
          </Stack>

          <Stack spacing={0.5}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Solicitud del cliente
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {order.customer_request || "-"}
            </Typography>
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Observaciones internas
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {order.internal_notes || "-"}
              </Typography>
              <Paper
                variant="outlined"
                sx={(theme) => ({
                  mt: 1,
                  px: 1,
                  py: 0.8,
                  borderStyle: "dashed",
                  borderColor: hasRegisteredDiagnostic
                    ? theme.palette.success.light
                    : theme.palette.warning.main,
                  bgcolor: hasRegisteredDiagnostic
                    ? "rgba(46, 125, 50, 0.05)"
                    : "rgba(237, 108, 2, 0.06)",
                })}
              >
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Stack direction="row" spacing={0.8} alignItems="center">
                    {hasRegisteredDiagnostic ? (
                      <TaskAltOutlined sx={{ fontSize: 17, color: "success.main" }} />
                    ) : null}
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      {hasRegisteredDiagnostic ? "Evaluacion registrada" : "Evaluacion diagnostico pendiente"}
                    </Typography>
                  </Stack>
                  {canManage ? (
                    <Button size="small" onClick={openDiagnosticDialog}>
                      {hasRegisteredDiagnostic ? "Editar" : "Completar/Editar"}
                    </Button>
                  ) : null}
                </Stack>
              </Paper>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Notas de resultado
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {order.result_notes || "-"}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Paper>

      <Paper
        variant="outlined"
        sx={(theme) => ({
          p: 2,
          borderColor: lines.length === 0 ? theme.palette.warning.main : theme.palette.divider,
          boxShadow:
            lines.length === 0
              ? `0 0 0 1px ${theme.palette.warning.light}`
              : "none",
        })}
      >
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Items grooming
            </Typography>
            <Button variant="outlined" onClick={openLineCreateDialog} disabled={!canEditItems}>
              Agregar item
            </Button>
          </Stack>

          {lines.length === 0 ? (
            <Alert severity="warning">
              Esta orden aun no tiene servicios cargados. Agrega al menos una linea para completar el flujo comercial.
            </Alert>
          ) : null}

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tipo</TableCell>
                <TableCell>Descripcion</TableCell>
                <TableCell>Cantidad</TableCell>
                <TableCell>Precio</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Duracion</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary">
                      Esta orden aun no tiene lineas.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                lines.map((line) => (
                  <TableRow key={line.id}>
                    <TableCell>
                      {LINE_TYPE_OPTIONS.find((entry) => entry.value === line.line_type)?.label || line.line_type}
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.2}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {line.description || "-"}
                        </Typography>
                        {line.notes ? (
                          <Typography variant="caption" color="text.secondary">
                            {line.notes}
                          </Typography>
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell>{line.quantity}</TableCell>
                    <TableCell>{toMoney(line.unit_price)}</TableCell>
                    <TableCell>{toMoney(line.total)}</TableCell>
                    <TableCell>{line.estimated_duration_minutes || 0} min</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          startIcon={<EditOutlined />}
                          onClick={() => openLineEditDialog(line)}
                          disabled={!canEditItems}
                        >
                          Editar
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteOutline />}
                          onClick={() => openDeleteLineDialog(line)}
                          disabled={!canEditItems}
                        >
                          Eliminar
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <Stack direction={{ xs: "column", md: "row" }} spacing={1.2} useFlexGap flexWrap="wrap">
            <Chip label={`Subtotal: ${toMoney(order.subtotal)}`} variant="outlined" />
            <Chip label={`Descuento: ${toMoney(order.discount_amount)}`} variant="outlined" />
            <Chip label={`Extras: ${toMoney(order.extra_amount)}`} variant="outlined" />
            <Chip label={`Total: ${toMoney(order.total)}`} color="primary" />
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Observaciones
            </Typography>
            <Chip label={`${notes.length} nota(s)`} variant="outlined" />
          </Stack>

          <TextField
            label="Nueva nota"
            value={noteDraft}
            onChange={(event) => setNoteDraft(event.target.value)}
            multiline
            minRows={3}
            fullWidth
            disabled={!canEditArtifacts}
          />
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={noteInternal}
                  onChange={(event) => setNoteInternal(event.target.checked)}
                  disabled={!canEditArtifacts}
                />
              }
              label="Nota interna"
            />
            <Button
              variant="contained"
              startIcon={<NoteAddOutlined />}
              onClick={handleCreateNote}
              disabled={!canEditArtifacts || noteSubmitting}
            >
              {noteSubmitting ? "Guardando..." : "Agregar nota"}
            </Button>
          </Stack>

          <Stack spacing={1}>
            {notes.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Aun no hay notas registradas.
              </Typography>
            ) : (
              notes.map((note) => (
                <Paper key={note.id} variant="outlined" sx={{ p: 1.25 }}>
                  <Stack spacing={0.8}>
                    <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        <Chip
                          size="small"
                          label={note.is_internal ? "Interna" : "Visible"}
                          color={note.is_internal ? "default" : "primary"}
                          variant={note.is_internal ? "outlined" : "filled"}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {note.author?.name || "Sin autor"} - {formatDateTime(note.created_at)}
                        </Typography>
                      </Stack>
                      {canEditNote(note) ? (
                        <Button size="small" startIcon={<EditOutlined />} onClick={() => openNoteEditDialog(note)}>
                          Editar
                        </Button>
                      ) : null}
                    </Stack>
                    <Typography variant="body2">{note.note || "-"}</Typography>
                  </Stack>
                </Paper>
              ))
            )}
          </Stack>
        </Stack>
      </Paper>

      <Dialog open={lineDialog.open} onClose={closeLineDialog} fullWidth maxWidth="sm">
        <DialogTitle>{lineDialog.mode === "edit" ? "Editar linea" : "Agregar linea"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <TextField
              select
              label="Tipo"
              value={lineForm.line_type}
              onChange={(event) =>
                setLineForm((prev) => ({
                  ...prev,
                  line_type: event.target.value,
                  service_type_id: event.target.value === "SERVICE" ? prev.service_type_id : "",
                }))
              }
              fullWidth
            >
              {LINE_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            {lineForm.line_type === "SERVICE" && (
              <Autocomplete
                options={serviceTypes}
                value={selectedLineService}
                getOptionLabel={(option) =>
                  option?.name
                    ? `${option.name} - Q${Number(option.base_price || 0).toFixed(2)}`
                    : ""
                }
                onChange={(_, option) =>
                  setLineForm((prev) => ({
                    ...prev,
                    service_type_id: option?.id ? String(option.id) : "",
                    description: option?.name || prev.description,
                    unit_price: option?.base_price != null ? String(option.base_price) : prev.unit_price,
                    estimated_duration_minutes:
                      option?.suggested_duration_minutes || option?.duration_minutes
                        ? String(option.suggested_duration_minutes || option.duration_minutes)
                        : prev.estimated_duration_minutes,
                  }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Servicio de catalogo" required fullWidth />
                )}
              />
            )}
            <TextField
              label="Descripcion"
              value={lineForm.description}
              onChange={handleLineFormChange("description")}
              fullWidth
            />
            <TextField
              label="Notas"
              value={lineForm.notes}
              onChange={handleLineFormChange("notes")}
              multiline
              minRows={2}
              fullWidth
            />
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
              <TextField
                label="Cantidad"
                value={lineForm.quantity}
                onChange={handleLineFormChange("quantity")}
                type="number"
                inputProps={{ min: 0.01, step: 0.01 }}
                fullWidth
              />
              <TextField
                label="Precio unitario"
                value={lineForm.unit_price}
                onChange={handleLineFormChange("unit_price")}
                type="number"
                inputProps={{ min: 0.01, step: 0.01 }}
                disabled={lineForm.line_type === "SERVICE" && !canLowerPrice}
                helperText={
                  lineForm.line_type === "SERVICE" && !canLowerPrice
                    ? "Tarifa base no editable."
                    : " "
                }
                fullWidth
              />
            </Stack>
            <Stack spacing={1}>
              <Typography variant="caption" color="text.secondary">
                Duracion estimada
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {DURATION_PRESETS.map((minutes) => (
                  <Button
                    key={minutes}
                    size="small"
                    variant={String(lineForm.estimated_duration_minutes) === String(minutes) ? "contained" : "outlined"}
                    onClick={() =>
                      setLineForm((prev) => ({ ...prev, estimated_duration_minutes: String(minutes) }))
                    }
                  >
                    {minutes} min
                  </Button>
                ))}
              </Stack>
              <TextField
                label="Custom (min)"
                value={lineForm.estimated_duration_minutes}
                onChange={handleLineFormChange("estimated_duration_minutes")}
                type="number"
                inputProps={{ min: 1, step: 1 }}
                fullWidth
              />
            </Stack>
            <TextField
              label={lineForm.line_type === "SERVICE" ? "Motivo de override (si baja tarifa)" : "Motivo del cargo manual"}
              value={lineForm.override_reason}
              onChange={handleLineFormChange("override_reason")}
              multiline
              minRows={2}
              required={["EXTRA", "ADJUSTMENT"].includes(lineForm.line_type)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeLineDialog} disabled={lineSubmitting}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSaveLine} disabled={lineSubmitting || !canEditItems}>
            {lineSubmitting ? "Guardando..." : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={noteDialog.open} onClose={closeNoteDialog} fullWidth maxWidth="sm">
        <DialogTitle>Editar nota</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <TextField
              label="Nota"
              value={noteDialog.note}
              onChange={(event) => setNoteDialog((prev) => ({ ...prev, note: event.target.value }))}
              multiline
              minRows={4}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={noteDialog.is_internal}
                  onChange={(event) =>
                    setNoteDialog((prev) => ({ ...prev, is_internal: event.target.checked }))
                  }
                />
              }
              label="Nota interna"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeNoteDialog} disabled={noteDialogSubmitting}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSaveNoteEdit} disabled={noteDialogSubmitting}>
            {noteDialogSubmitting ? "Guardando..." : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDialog.open} onClose={closeConfirmDialog} fullWidth maxWidth="sm">
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Typography variant="body2">{confirmDialog.description}</Typography>
            {["cancel-order", "void-sale", "reduce-total"].includes(confirmDialog.type) ? (
              <TextField
                label={
                  confirmDialog.type === "cancel-order"
                    ? "Razon de cancelacion"
                    : confirmDialog.type === "void-sale"
                      ? "Razon de anulacion"
                      : "Razon de reduccion"
                }
                value={confirmDialog.value}
                onChange={(event) => setConfirmDialog((prev) => ({ ...prev, value: event.target.value }))}
                multiline
                minRows={3}
                fullWidth
              />
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog} disabled={Boolean(actionSubmitting)}>
            Cerrar
          </Button>
          <Button
            variant="contained"
            color={confirmDialog.color}
            onClick={handleConfirmDialogAction}
            disabled={Boolean(actionSubmitting)}
          >
            {Boolean(actionSubmitting) ? "Procesando..." : confirmDialog.confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>

      <GroomingDiagnosticDialog
        open={diagnosticDialog}
        branchId={branchId}
        orderId={order?.id || null}
        initialOrder={order}
        canManage={canManage}
        onClose={() => setDiagnosticDialog(false)}
        onContinue={(orderData) => {
          if (orderData?.id === order?.id) {
            setOrder(orderData);
          } else {
            loadOrder();
          }
          setDiagnosticDialog(false);
        }}
      />

      <SalePaymentDialog
        open={payDialogOpen}
        onCancel={() => setPayDialogOpen(false)}
        onConfirm={handleConfirmPay}
        total={Number(selectedSale?.total || 0)}
        itemsCount={lines.length}
        hasOpenCashSession={hasOpenCashSession}
        transferAccounts={transferAccounts}
        loadingTransferAccounts={loadingTransferAccounts}
        submitting={paySubmitting}
      />

      <CustomSnackBar open={snackOpen} setOpen={setSnackOpen} message={snackMsg} severity={snackSev} />
    </Box>
  );
}

export default GroomingOrderDetailPage;
